import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { invalidateQueriesTransactions } from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  CreateBuyBackEventRequest,
  GetBuyBackByIdExtendedResponse,
  GetBuyBackEventListResponse,
  GetBuyBackEventListV1SrBuyBackEventsGetParams,
  GetBuybackV1SrBuyBackEventsIdGetParams,
} from 'api';

export const BUY_BACK_LIST_QUERY_KEY = 'vestra/buy-back-list';
export const BUY_BACK_BY_ID_QUERY_KEY = 'vestra/buy-back-by=id';

export const useBuyBackEventsQuery = (
  payload: GetBuyBackEventListV1SrBuyBackEventsGetParams,
  disabled = false,
  register_id?: number,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery<GetBuyBackEventListResponse>({
    queryKey: [BUY_BACK_LIST_QUERY_KEY, JSON.stringify(data), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getBuyBackEventListV1SrBuyBackEventsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useBuyBackEventByIdQuery = (
  payload: GetBuybackV1SrBuyBackEventsIdGetParams,
  disabled = false,
  register_id?: number,
) => {
  return useQuery<GetBuyBackByIdExtendedResponse>({
    queryKey: [BUY_BACK_BY_ID_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getBuybackV1SrBuyBackEventsIdGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useBuyBackEventArchiveByIdMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.archiveBuybackV1SrBuyBackEventsIdPost({ id }),
        {
          loading: 'Buy back event archiving...',
          success: 'Buy back event archived!',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({
        updateDividend: false,
      });
    },
  });
};

export const useCreateBuyBackMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateBuyBackEventRequest) => {
      const response = await withErrorHandling(
        api.v1.createBuyBackEventV1SrBuyBackEventsPost(payload),
        {
          loading: 'Creating buy back event...',
          success: 'Event created successfully',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({
        updateDividend: false,
      });
      onSuccess && onSuccess();
    },
  });
};
